import { z } from 'zod';

import {
	PROGRAM_SECTIONS,
	type ProgramOwnerCheckoutStatesSupportedDto,
	type ProgramOwnerServiceConfig,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
	StateAbbreviation,
	StateAbbreviationSchema,
	StateName,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateCheckoutStatesSupported } from '../../../../api/configuration/services/useUpdateCheckoutStatesSupported';
import { useServiceProgramInfo } from '../../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../../config-mgmt/FormSection';

/**
 * Form schema for states supported - uses array of state abbreviations
 * This is transformed to/from the record format for the API
 */
const statesSupportedFormSchema = z.object({
	states: z.array(StateAbbreviationSchema).default([]),
});

type StatesSupportedFormData = z.infer<typeof statesSupportedFormSchema>;

/**
 * Transform states supported record to array of state abbreviations for form
 */
function recordToArray(statesSupported: ProgramOwnerCheckoutStatesSupportedDto): string[] {
	return Object.keys(statesSupported);
}

/**
 * Transform array of state abbreviations to states supported record for API
 */
function arrayToRecord(stateAbbreviations: string[]): ProgramOwnerCheckoutStatesSupportedDto {
	const result: ProgramOwnerCheckoutStatesSupportedDto = {};
	for (const abbrev of stateAbbreviations) {
		const stateName = StateName[abbrev as keyof typeof StateName];
		if (stateName) {
			result[abbrev as keyof typeof StateAbbreviation] = {
				stateFullName: stateName,
			};
		}
	}
	return result;
}

/**
 * Helper function to get initial form data for checkout states supported
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): StatesSupportedFormData {
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const checkoutConfig = programOwnerConfig?.checkout;

	const statesSupported = checkoutConfig?.statesSupported ?? {};
	const states = recordToArray(statesSupported);

	return { states: states as (keyof typeof StateAbbreviation)[] };
}

/**
 * Create options array from StateAbbreviation and StateName
 */
function createStateOptions() {
	return Object.entries(StateAbbreviation)
		.map(([abbrev, value]) => ({
			id: value,
			label: StateName[abbrev as keyof typeof StateName] || abbrev,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));
}

export function CheckoutStatesSupportedForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(
		PROGRAM_SECTIONS.CHECKOUT,
		SUBSECTIONS.CHECKOUT_STATES_SUPPORTED,
	);
	const updateCheckoutStatesSupported = useUpdateCheckoutStatesSupported();

	// Early return if subsection is not part of the program or is Ekho-only
	if (!programInfo.shouldRender) {
		return null;
	}

	const stateOptions = createStateOptions();

	const handleSubmit = async (data: StatesSupportedFormData): Promise<{ isSuccess: boolean }> => {
		try {
			const statesSupported = arrayToRecord(data.states);
			await updateCheckoutStatesSupported.mutateAsync(statesSupported);
			return { isSuccess: true };
		} catch (error) {
			console.error('Failed to update checkout states supported:', error);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.PROGRAM,
				section: PROGRAM_SECTIONS.CHECKOUT,
				subsection: SUBSECTIONS.CHECKOUT_STATES_SUPPORTED,
			}}
			form={{
				schema: statesSupportedFormSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='States supported'
			description='Select the states where buyers are allowed to purchase. Buyers outside supported states will be unable to complete checkout.'
			formTitle='Eligible states'>
			<Form.Field.ChipPicker
				schemaField='states'
				options={stateOptions}
				placeholder='Search for a state'
				emptyText='No states selected'
				showResultsOnFocus
			/>
		</FormSection>
	);
}

export default CheckoutStatesSupportedForm;
